import { useTimezone } from '@app/core/hooks';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Box } from '@mui/material';
import { useIntl } from 'react-intl';
import { dateSelectorPresetMessages } from './date-selector-messages';
import {
  type DateSelectorPresetId,
  DAY_OFFSET_PRESETS,
  getPresetDate,
} from './date-selector-presets';

export interface SingleDateSelectorTriggerLabelProps {
  /** Currently selected date (for custom "Seleccionar fecha" and display). */
  value?: Date | null;
  /** Current preset id (determines which label to show). */
  presetId?: DateSelectorPresetId | null;
  /** Preset used when presetId is not provided. */
  defaultPreset?: DateSelectorPresetId;
}

const DEFAULT_PRESET: DateSelectorPresetId = 'today';

/** Short format with weekday for previous days and custom date: e.g. "lun. 16 feb." */
const shortDateFormatWithWeekday = {
  weekday: 'short' as const,
  month: 'short' as const,
  day: 'numeric' as const,
};

/** Capitalizes only the first character of the string (e.g. "lun. 16 feb." → "Lun. 16 feb."). */
function capitalizeFirstLetter(str: string): string {
  if (!str) {
    return str;
  }

  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Renders the single-date selector trigger content: calendar icon plus
 * a label from the current preset or selected date (e.g. "Hoy", "Ayer", "16 feb.", or custom date).
 */
export function SingleDateSelectorTriggerLabel({
  value,
  presetId,
  defaultPreset = DEFAULT_PRESET,
}: SingleDateSelectorTriggerLabelProps) {
  const { $t, formatDate } = useIntl();
  const tz = useTimezone();
  const effectivePreset = presetId ?? defaultPreset;

  const label =
    effectivePreset === 'selectDate' && value
      ? capitalizeFirstLetter(formatDate(value, shortDateFormatWithWeekday) ?? '')
      : DAY_OFFSET_PRESETS.includes(effectivePreset)
        ? (() => {
            const date = getPresetDate(effectivePreset, tz);
            return date
              ? capitalizeFirstLetter(formatDate(date, shortDateFormatWithWeekday) ?? '')
              : $t(dateSelectorPresetMessages[effectivePreset]);
          })()
        : $t(dateSelectorPresetMessages[effectivePreset]);

  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      <CalendarMonthIcon fontSize="small" />
      {label}
    </Box>
  );
}
